export interface DragDropImagesResult {
  files: File[]
  urls: string[]
  rejected: Array<{ reason: string; count: number }>
}

export const ACCEPTED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'] as const

const ACCEPTED_IMAGE_MIME_SET = new Set<string>(ACCEPTED_IMAGE_MIME_TYPES)

export const IMAGE_UPLOAD_RATE_LIMIT_MESSAGE =
  'Upload limit reached (100/hour). Try again later.'

export function isFileDrag(dataTransfer: DataTransfer | null): boolean {
  if (!dataTransfer) return false
  if (dataTransfer.types?.includes('Files')) return true
  return (dataTransfer.files?.length ?? 0) > 0
}

export function parseImageDrop(
  dataTransfer: DataTransfer | null
): DragDropImagesResult {
  const files: File[] = []
  const rejectedMap = new Map<string, number>()
  const seen = new Set<string>()

  const registerRejected = (reason: string) => {
    rejectedMap.set(reason, (rejectedMap.get(reason) ?? 0) + 1)
  }

  const registerFile = (file: File | null) => {
    if (!file) return
    const key = `${file.name}-${file.size}-${file.type}-${file.lastModified}`
    if (seen.has(key)) return
    seen.add(key)

    if (!ACCEPTED_IMAGE_MIME_SET.has(file.type)) {
      registerRejected('unsupported')
      return
    }

    files.push(file)
  }

  if (dataTransfer?.items?.length) {
    for (const item of Array.from(dataTransfer.items)) {
      if (item.kind !== 'file') continue
      registerFile(item.getAsFile())
    }
  }

  if (dataTransfer?.files?.length) {
    for (const file of Array.from(dataTransfer.files)) {
      registerFile(file)
    }
  }

  const rejected = Array.from(rejectedMap.entries()).map(([reason, count]) => ({
    reason,
    count,
  }))

  return { files, urls: [], rejected }
}

export function getUploadErrorMessage(error?: string): string {
  if (!error) return 'Failed to upload image.'
  if (error.toLowerCase().includes('upload limit')) {
    return IMAGE_UPLOAD_RATE_LIMIT_MESSAGE
  }
  return error
}

interface UploadSelectionSummaryOptions {
  invalidTypeCount?: number
  oversizeCount?: number
  overflowCount?: number
  uploadFailureMessage?: string
}

export function summarizeUploadSelectionError({
  invalidTypeCount = 0,
  oversizeCount = 0,
  overflowCount = 0,
  uploadFailureMessage,
}: UploadSelectionSummaryOptions): string {
  if (uploadFailureMessage) {
    return uploadFailureMessage
  }

  if (invalidTypeCount > 0 && oversizeCount > 0) {
    return 'Some files were skipped. Only JPG and PNG under 10MB are supported.'
  }

  if (oversizeCount > 0) {
    return 'Some files were skipped. Files must be 10MB or smaller.'
  }

  if (invalidTypeCount > 0) {
    return 'Some files were skipped. Only JPG and PNG are supported.'
  }

  if (overflowCount > 0) {
    return 'Upload limit reached (200 images)'
  }

  return ''
}
